/**
 * Monolith 디자인 토큰 — 홈페이지 2안(콜드 라이트) 공통 값.
 *
 * 출처: claudeFiles/judahsWeb/homepage-mockups/2안-monolith (2026-09-02 목업).
 * 페르소나 SSOT: claudeFiles/judahsWeb/PERSONA.md — 베이스는 모노크롬, 액센트는 두지 않는다.
 * 2안은 액센트 0 — 블랙 CTA 하나가 유일한 강조다.
 *
 * 페이지·네비·푸터가 전부 이 값을 참조한다. 색·폰트를 바꿀 때는 여기만 고친다.
 */

export const mono = {
	/** 페이지 그라운드 */
	bg: '#F7F8FA',
	/** 카드·패널 */
	surface: '#FFFFFF',
	/** 본문 잉크 */
	ink: '#16181B',
	/** 블랙 CTA 배경 */
	ctaBg: '#101114',
	/** 보조 텍스트 */
	dim: '#5F6670',
	/** 키커·캡션 */
	faint: '#9AA1AA',
	/** 헤어라인 */
	line: '#E5E7EA',
	/** 비활성 보더(칩·구분자) */
	lineSoft: '#D8DBDF',
	/** 폼 밑줄 */
	lineInput: '#C6CACF',
	/** 에러 텍스트 (폼 검증 메시지 전용) */
	error: '#B42318',
} as const

export const monoFont = {
	/** 본문·타이틀 */
	sans: '"Archivo", "Helvetica Neue", Helvetica, Arial, sans-serif',
	/** 제품 워드마크 "KOINOS" — 워드마크 규정(Science Gothic Bold) */
	koinos: '"Science Gothic", "Archivo", sans-serif',
	/** 제품 모델라인 "PLS 8" — 워드마크 규정(MuseoModerno Thin) */
	model: '"MuseoModerno", "Archivo", sans-serif',
	/** 제품 워드마크 "KEDEM 6" — 워드마크 규정(Iceland 고정, ×1.43 보정은 KedemWordmark 가 건다) */
	kedem: '"Iceland", "Archivo", sans-serif',
} as const

/** 1440 기준 좌우 여백 72px. 좁은 화면에서는 줄어든다. */
export const monoGutter = 'clamp(20px, 5vw, 72px)'

/** 콘텐츠 최대폭. 목업은 1440 고정이었고, 그 이상에서는 가운데 정렬한다. */
export const monoMaxWidth = 1440

/**
 * 페이지 공통 스타일 조각. 각 페이지 css() 안에서 스프레드해서 쓴다.
 * 클래스 이름은 목업의 역할명을 그대로 옮겼다.
 */
export const monoShared = {
	fontFamily: monoFont.sans,
	color: mono.ink,
	backgroundColor: mono.bg,
	textAlign: 'left' as const,
	WebkitFontSmoothing: 'antialiased' as const,

	'h1, h2, h3, p': {
		margin: 0,
	},

	a: {
		color: mono.ink,
		textDecoration: 'none',
	},

	/** 좌우 여백 + 최대폭 */
	'.mnWrap': {
		width: '100%',
		maxWidth: monoMaxWidth,
		margin: '0 auto',
		paddingLeft: monoGutter,
		paddingRight: monoGutter,
		boxSizing: 'border-box' as const,
	},

	/** 헤어라인으로 구분되는 밴드. 여백이 이 안의 주된 표현 수단이다. */
	'.mnBand': {
		borderTop: `1px solid ${mono.line}`,
		padding: 'clamp(64px, 8vw, 110px) 0',
	},

	/** 대문자 키커 */
	'.mnKicker': {
		fontSize: 12,
		letterSpacing: '.3em',
		textTransform: 'uppercase' as const,
		color: mono.faint,
		fontWeight: 500,
	},

	/** 블랙 CTA — 페이지 전체에서 유일한 강조 */
	'.mnCta': {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 54,
		padding: '0 44px',
		background: mono.ctaBg,
		color: '#FFFFFF',
		fontFamily: monoFont.sans,
		fontSize: 15,
		fontWeight: 500,
		borderRadius: 2,
		border: 0,
		cursor: 'pointer',
		whiteSpace: 'nowrap' as const,
		'&:hover': {
			background: mono.ink,
		},
	},

	/** 아웃라인 CTA — 보조 동작 */
	'.mnCtaGhost': {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 50,
		padding: '0 36px',
		background: 'transparent',
		color: mono.ink,
		fontFamily: monoFont.sans,
		fontSize: 14,
		fontWeight: 500,
		border: `1px solid ${mono.ink}`,
		borderRadius: 2,
		cursor: 'pointer',
		whiteSpace: 'nowrap' as const,
	},

	/** 밑줄 텍스트 링크 */
	'.mnTextLink': {
		fontSize: 14,
		fontWeight: 500,
		color: mono.ink,
		borderBottom: `1px solid ${mono.ink}`,
		paddingBottom: 2,
		background: 'transparent',
		border: 0,
		borderRadius: 0,
		cursor: 'pointer',
		fontFamily: monoFont.sans,
	},

	/** 3열 주장 그리드 (Home·PLS 8 공용) */
	'.mnClaims': {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		gap: 64,

		'.mnClaim': {
			display: 'flex',
			flexDirection: 'column' as const,
			gap: 16,

			h3: {
				fontSize: 26,
				lineHeight: 1.3,
				fontWeight: 400,
				color: mono.ink,
			},
			p: {
				fontSize: 15,
				lineHeight: 1.65,
				fontWeight: 300,
				color: mono.dim,
			},
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 40,
		},
	},
}

/**
 * 폼 조각 — DESIGN_SYSTEM §5 "폼 입력" 규격.
 *
 * 밑줄 입력 · 키커 라벨 · 검증 메시지. Contact 가 페이지 안에서 직접 쓰던 값을
 * 계정/인증 6개 페이지가 같이 쓰게 꺼낸 것이다. 값이 갈리면 이 쪽이 SSOT.
 * 페이지 css() 안에서 monoShared 뒤에 스프레드한다.
 */
export const monoForm = {
	/** 라벨 = 키커보다 좁은 자간(.2em) */
	'.mnLabel': {
		fontSize: 12,
		letterSpacing: '.2em',
		textTransform: 'uppercase' as const,
		color: mono.faint,
		fontWeight: 500,
	},

	'.mnField': {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 12,
	},

	/** 2열 폼 행 — 600px 에서 1열 */
	'.mnFormRow2': {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: 40,

		'@media (max-width: 600px)': {
			gridTemplateColumns: '1fr',
		},
	},

	/** 밑줄만 있는 입력. 박스·라운드 없음 */
	'.mnInput': {
		width: '100%',
		height: 46,
		padding: 0,
		border: 0,
		borderBottom: `1px solid ${mono.lineInput}`,
		borderRadius: 0,
		background: 'transparent',
		fontFamily: monoFont.sans,
		fontSize: 16,
		fontWeight: 300,
		color: mono.ink,
		outline: 'none',
		boxSizing: 'border-box' as const,

		'&:focus': {
			borderBottomColor: mono.ink,
		},
		'&.error': {
			borderBottomColor: mono.error,
		},
	},

	/** 폼 검증 메시지 — error 색은 여기서만 쓴다 */
	'.mnError': {
		fontSize: 12,
		color: mono.error,
		fontWeight: 400,
	},

	/** 체크박스 한 줄 (뉴스레터 수신 동의) */
	'.mnCheck': {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 12,
		padding: 0,
		border: 0,
		background: 'transparent',
		cursor: 'pointer',
		textAlign: 'left' as const,

		'.mnCheckBox': {
			flexShrink: 0,
			width: 16,
			height: 16,
			marginTop: 3,
			border: `1px solid ${mono.lineInput}`,
			borderRadius: 0,
			position: 'relative' as const,

			'&.on': {
				borderColor: mono.ink,
				background: mono.ink,
			},
			'&.on::after': {
				content: '""',
				position: 'absolute' as const,
				left: 4,
				top: 1,
				width: 4,
				height: 8,
				border: '1px solid #FFFFFF',
				borderWidth: '0 1px 1px 0',
				transform: 'rotate(45deg)',
			},
		},

		'.mnCheckLabel': {
			fontSize: 14,
			fontWeight: 300,
			lineHeight: 1.6,
			color: mono.dim,
		},

		'&:hover .mnCheckLabel': {
			color: mono.ink,
		},
	},

	/** 스펙 테이블 행 — 라벨(고정폭) + 값, 하단 헤어라인 */
	'.mnSpecRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 200px) minmax(0, 1fr)',
		gap: 24,
		alignItems: 'center',
		padding: '17px 0',
		borderBottom: `1px solid ${mono.line}`,

		'.mnSpecLabel': {
			fontSize: 13,
			letterSpacing: '.14em',
			textTransform: 'uppercase' as const,
			color: mono.faint,
			fontWeight: 500,
		},
		'.mnSpecValue': {
			fontSize: 16,
			fontWeight: 300,
			color: mono.ink,
		},

		'@media (max-width: 700px)': {
			gridTemplateColumns: '1fr',
			gap: 8,
			alignItems: 'flex-start',
		},
	},

	/** 12~13px 대문자 메타 (주문 상태·날짜) */
	'.mnMeta': {
		fontSize: 12,
		letterSpacing: '.2em',
		textTransform: 'uppercase' as const,
		fontWeight: 500,
		color: mono.faint,
	},
}
